import React, { useState, useEffect } from 'react';
import './accueil.css';
import Sidebar from '../sidebar/Sidebar';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
} from 'chart.js';
import { FaUsers, FaBullhorn, FaCalendarAlt, FaEnvelope } from 'react-icons/fa';
import { userService, annonceService, evenementService, contactService } from '../../../api';

// Enregistrement des composants de ChartJS
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const AccueilAdmin = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCount = (response) => {
    if (!response) return 0;
    if (Array.isArray(response)) return response.length;
    if (Array.isArray(response.data)) return response.data.length;
    if (response.data?.data && Array.isArray(response.data.data)) return response.data.data.length;
    if (response.data?.meta?.total) return response.data.meta.total;
    if (typeof response.data === 'object') return 1;
    return 0;
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const [users, annonces, events, contacts] = await Promise.all([
          userService.getAll(),
          annonceService.getAll(),
          evenementService.getAll(),
          contactService.getAll()
        ]);

        if (!users || !annonces || !events || !contacts) {
          throw new Error('Une ou plusieurs réponses API sont invalides');
        }

        setStats([
          {
            title: "Utilisateurs",
            value: getCount(users),
            icon: <FaUsers />,
            iconBg: "#2ecc71",
            color: 'rgba(46, 204, 113, 0.2)',
            borderColor: 'rgb(46, 204, 112)'
          },
          {
            title: "Annonces",
            value: getCount(annonces),
            icon: <FaBullhorn />,
            iconBg: "#3498db",
            color: 'rgba(52, 152, 219, 0.2)',
            borderColor: 'rgba(52, 152, 219, 1)'
          },
          {
            title: "Événements",
            value: getCount(events),
            icon: <FaCalendarAlt />,
            iconBg: "#9b59b6",
            color: 'rgba(155, 89, 182, 0.2)',
            borderColor: 'rgba(155, 89, 182, 1)'
          },
          {
            title: "Contacts",
            value: getCount(contacts),
            icon: <FaEnvelope />,
            iconBg: "#e74c3c",
            color: 'rgba(231, 76, 60, 0.2)',
            borderColor: 'rgba(231, 76, 60, 1)'
          }
        ]);
      } catch (err) {
        console.error("Erreur API:", err);
        setError(err.message || "Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const generateChartData = (label, color, borderColor) => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin'];
    return {
      labels: months,
      datasets: [{
        label,
        data: months.map(() => Math.floor(Math.random() * 1000)),
        fill: true,
        backgroundColor: color,
        borderColor: borderColor,
        tension: 0.4,
        borderWidth: 2,
        pointBackgroundColor: borderColor,
        pointRadius: 4
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 200 } }
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <div className="admin-content">
          <div className="loading-spinner"></div>
          <p>Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <div className="admin-content">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              <span>Réessayer</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Sidebar />
      <div className="admin-content">
        <header className="dashboard-header">
          <h1>Tableau de bord administrateur</h1>
        </header>

        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-icon" style={{ backgroundColor: stat.iconBg }}>
                {stat.icon}
              </div>
              <div className="stat-content">
                <h3>{stat.title}</h3>
                <p className="stat-value">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="charts-container">
          {stats.map((stat, index) => (
            <div key={index} className="chart-item">
              <h3 className="chart-title">{stat.title}</h3>
              <div className="chart-wrapper">
                <Line
                  data={generateChartData(stat.title, stat.color, stat.borderColor)}
                  options={chartOptions}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccueilAdmin;